import { Component, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { DSM3ModalService, DSM3SelectOption } from '@eredes/dsm3';
import { forkJoin, Observable, of } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';

import { CpeFilterViewModel } from './cpe-filter.view-model';

import { EdpdDropdownItem } from 'app/_core/models/edpd-dropdown-item.model';
import { RequestCpeService } from 'app/_core/services/request-cpe.service';
import { EdpValidators } from 'app/_shared/edp-validators/edp-validators';

export interface Street {
  street_code: string;
  street_name: string;
}

@Component({
  selector: 'app-cpe-filter',
  templateUrl: './cpe-filter.component.html',
  styleUrls: ['./cpe-filter.component.scss'],
  standalone: false,
})
export class RequestCpeFilterComponent implements OnInit {
  // Modal custom templates
  @ViewChild('templateCPEfound') templateModalCPEfound: TemplateRef<any>;
  @ViewChild('templateCPEnotFound') templateModalCPEnotFound: TemplateRef<any>;

  viewModel = new CpeFilterViewModel();
  isLoadingFractions: boolean;

  get f() {
    return this.viewModel.cpeForm.controls;
  }

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly requestCpeService: RequestCpeService,
    private readonly modalService: DSM3ModalService,
    private readonly viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit() {
    this.creatForm('zip');

    this.initFiltersLists().subscribe((res) => {
      if (res) {
        this.viewModel.isFiltersReady = true;
      }
    });
  }

  /**
   * Sets filter value to default when user does "Limpar" on filter dropdown
   */
  onDropdownClear() {}

  /**
   * Return the value of the legal information checkbox
   * @returns
   */
  isChecked(): boolean {
    return this.viewModel.declarationsTerms;
  }

  /**
   * Check the value of the legal information checkbox
   * @param $event
   */
  onChecked($event): void {
    this.viewModel.declarationsTerms = $event.srcElement.checked;
  }

  private initFiltersLists(): Observable<boolean> {
    const getDistricts$ = this.requestCpeService.getDistricts({ entity: 'Location Districts' });
    const getCounties$ = this.requestCpeService.getCounties({ entity: 'Location Counties' });
    const getCounters$ = this.requestCpeService.getCounters({ entity: 'Masterdate Counters' });
    forkJoin([getDistricts$, getCounties$, getCounters$]).subscribe((results) => {
      this.viewModel.districtList = results[0] ? this.setFilter(results[0]) : [];
      this.viewModel.districtList$.next(this.viewModel.districtList);

      this.viewModel.countyList = results[1] ? this.setFilter(results[1]) : [];
      this.viewModel.countyList$.next(this.viewModel.countyList);

      this.viewModel.counterList = results[2] ? this.setFilter(results[2]) : [];
      this.viewModel.counterList$.next(this.viewModel.counterList.sort((a, b) => a.label.localeCompare(b.label)));

      this.viewModel.districtListAll = [...this.viewModel.districtList];
      this.viewModel.countyListAll = [...this.viewModel.countyList];

      return true;
    });

    return of(false);
  }

  /**
   * Returns the fields of each form according to the selected tab
   * @param value
   * @returns
   */
  private getForm(value: string): any {
    switch (value) {
      case 'address': {
        this.cleanBehaviorSubject();

        return this.setFormAddress();
      }
      case 'counter': {
        this.cleanBehaviorSubject();

        return this.setFormCounter();
      }
      default: {
        this.cleanBehaviorSubject();

        return this.setFormZip();
      }
    }
  }

  /**
   * Build form dynamically, according to the selected tab type
   * @param initWay
   */
  private creatForm(initWay?: string): any {
    this.viewModel.cpeForm = this.fb.group({
      way: [initWay, []],
      acceptTerms: [false, [Validators.requiredTrue]],
      ...this.getForm(initWay),
    });
    this.viewModel.cpeForm.markAsPristine();
    this.checkAddressValues();

    this.viewModel.cpeForm.get('way').valueChanges.subscribe((val) => {
      this.removeAllControls();
      this.viewModel.cpeForm.markAsPristine();
      this.creatForm(val);
    });
  }

  private removeAllControls(): void {
    this.viewModel.cpeForm.removeControl('zipcode');
    this.viewModel.cpeForm.removeControl('door');
    this.viewModel.cpeForm.removeControl('floor');
    this.viewModel.cpeForm.removeControl('nif');
    this.viewModel.cpeForm.removeControl('district');
    this.viewModel.cpeForm.removeControl('county');
    this.viewModel.cpeForm.removeControl('parish');
    this.viewModel.cpeForm.removeControl('street');
    this.viewModel.cpeForm.removeControl('counter');
    this.viewModel.cpeForm.removeControl('reference');
    this.viewModel.cpeForm.get('acceptTerms').setValue(false);
    this.viewModel.declarationsTerms = false;
    this.viewModel.floorList$.next([]);
  }

  private setFormZip(): any {
    return {
      zipcode: [null, [Validators.required, EdpValidators.postalCode]],
      door: [null, [Validators.required]],
      floor: [null],
      nif: [null],
    };
  }

  private setFormAddress(): any {
    return {
      district: [null, [Validators.required]],
      county: [null, [Validators.required]],
      street: [null, [Validators.required]],
      door: [null, [Validators.required]],
      floor: [null],
      nif: [null],
    };
  }

  private setFormCounter(): any {
    return {
      counter: [null, [Validators.required]],
      reference: [null, [Validators.required]],
    };
  }

  private setFilter(arry): EdpdDropdownItem[] {
    const newArry = [];
    arry.forEach((x) => {
      newArry.push({
        value: x.value || x.code,
        label: x.title || x.name,
      });
    });

    return newArry;
  }

  /**
   * @param arry
   * @param type
   */
  updateDropVal(arry: any, type: string) {
    if (arry.length > 0) {
      this[type] = arry.join(' ');
      if (type === 'filterDistrict') {
        this.viewModel.cpeForm.get('county').setValue(null);
        this.viewModel.cpeForm.get('street').setValue(null);
        this.rebuildFilterCountie(arry);
      }
      if (type === 'filterCounty') {
        this.viewModel.cpeForm.get('street').setValue(null);
        this.buildFilterStreet(arry.toString());
      }
      if (type === 'filterCounter') {
        this.viewModel.cpeForm.get('counter').setValue(arry.toString());
      }
      if (type === 'filterStreet') {
        this.viewModel.cpeForm.get('floor').setValue(null);
        this.viewModel.floorList$.next([]);
        this.buildFilterFloor();
      }
    } else {
      this[type] = '';
      if (type === 'filterDistrict') {
        this.viewModel.countyList = this.viewModel.countyListAll;
        this.viewModel.cpeForm.get('district').setValue(null);
        this.viewModel.cpeForm.get('county').setValue(null);
        this.viewModel.cpeForm.get('street').setValue(null);
      }
      if (type === 'filterCounty') {
        this.viewModel.cpeForm.get('street').setValue(null);
      }
      if (type === 'filterStreet') {
        this.viewModel.cpeForm.get('floor').setValue(null);
        this.viewModel.floorList$.next([]);
      }
    }
  }

  /**
   * Rebuild filter county with counties of district selected
   * @param arry
   */
  private rebuildFilterCountie(arry): void {
    const newArry = [];
    arry.forEach((dist) => {
      const p = this.viewModel.countyListAll.filter((x) => {
        return x.value.startsWith(dist);
      });
      newArry.push(...p);
    });
    this.viewModel.filterCountySelected = [];
    this.viewModel.filterCounty = '';
    this.viewModel.cpeForm.get('county').setValue(null);
    this.viewModel.countyList = newArry.sort((a, b) => a.label.localeCompare(b.label));

    this.viewModel.countyList$.next(this.viewModel.countyList);
  }

  /**
   * Configure floor list options by selected street
   * @param street - selected street
   */
  private buildFilterFloor(): void {
    const { street, door } = this.viewModel.cpeForm.value;
    this.viewModel.selectedStreet = street;

    const streetSelected = this.viewModel.streets.find((item) => {
      return item.street_name === street;
    });

    if (streetSelected && door) {
      const args = {
        street_code: streetSelected.street_code,
        house_number: door,
      };

      this.requestCpeService.getFloors(args).subscribe((results) => {
        const list = [];
        results.contentResponse.floors.forEach((item) => {
          list.push({
            value: item,
            label: item,
          });
        });
        this.viewModel.floorList$.next(list);
      });
    }
  }

  /**
   * Build filter street with parish id selected
   * @param id string
   */
  private buildFilterStreet(id: string): void {
    const args = {
      entity: 'Location Streets',
    };
    this.requestCpeService.getStreets(args, id).subscribe(
      (results) => {
        this.viewModel.streets = results;

        const list = [];
        results.forEach((item) => {
          list.push({
            value: item.street_name,
            label: item.street_name,
          });
        });

        this.viewModel.streetList$.next(list);
      },
      (err) => {
        this.viewModel.streetList$.next([]);
        this.viewModel.streets = [];
        this.viewModel.cpeForm.get('street').reset();
      }
    );
  }

  /**
   * @param field FormControl
   * @returns
   */
  isRequired(field: UntypedFormControl): boolean {
    if (field.validator !== null) {
      const validator = field.validator({} as AbstractControl);

      return validator && validator.required;
    }

    return false;
  }

  /*
   * Reset the BehaviorSubjects values when the form tab change
   * (tabs- zip/address/counter)
   */
  cleanBehaviorSubject(): void {
    this.viewModel.streetList$.next([]);
    this.viewModel.streets = [];
    this.viewModel.countyList$.next(this.viewModel.countyListAll);
  }

  /*
   * Detect when BehaviorSubjects value change
   */
  private checkAddressValues(): void {
    if (this.viewModel.cpeForm.get('district')) {
      this.viewModel.cpeForm.get('district').valueChanges.subscribe((value) => {
        this.updateDropVal([value], 'filterDistrict');
      });
    }

    if (this.viewModel.cpeForm.get('county')) {
      this.viewModel.cpeForm.get('county').valueChanges.subscribe((value) => {
        if (value) {
          this.updateDropVal([value], 'filterCounty');
        }
      });
    }

    if (this.viewModel.cpeForm.get('street')) {
      this.viewModel.cpeForm.get('street').valueChanges.subscribe((value) => {
        if (this.viewModel.selectedStreet !== value) {
          this.updateDropVal([value], 'filterStreet');
        }
      });
    }

    if (this.viewModel.cpeForm.get('door')) {
      this.viewModel.cpeForm
        .get('door')
        .valueChanges.pipe(debounceTime(500))
        .subscribe(() => {
          this.viewModel.floorList$.next([]);
          this.buildFilterFloor();
        });
    }
  }

  /**
   * Clean Form
   */
  onReset(): void {
    const way = this.viewModel.cpeForm.get('way').value;
    this.removeAllControls();
    this.viewModel.cpeForm.markAsPristine();
    this.viewModel.cpeForm.reset();
    this.cleanBehaviorSubject();
    setTimeout(() => {
      this.creatForm(way);
    }, 10);
  }

  /*
   * On form submit make new request to search CPE code
   */
  onSubmit(): void {
    this.viewModel.filters = this.viewModel.cpeForm.value;
    this.getCpeData();
  }

  /*
   * Request for search cpe
   * when a tab is filled the button for search become available
   * on click is checked and save the filters for the correspondig tab
   * after have the corresponding filters make the request
   * after recive response open moodal ddepending of the response
   */
  getCpeData(): void {
    const args: any = {
      entity: 'Masterdata CPE List',
      way: this.viewModel.filters.way,
    };

    if (this.viewModel.cpeForm.value.way === 'zip') {
      if (this.viewModel.filters.zipcode) {
        args.zipcode = this.viewModel.filters.zipcode.slice(0, 4) + '-' + this.viewModel.filters.zipcode.slice(4);
      }
      if (this.viewModel.filters.door) {
        args.door = this.viewModel.filters.door;
      }
      if (this.viewModel.filters.floor) {
        args.floor = this.viewModel.filters.floor;
      }
      if (this.viewModel.filters.nif) {
        args.nif = this.viewModel.filters.nif;
      }
    } else if (this.viewModel.cpeForm.value.way === 'address') {
      if (this.viewModel.filters.district) {
        args.district = this.viewModel.filters.district;
      }
      if (this.viewModel.filters.county) {
        args.county = this.viewModel.filters.county;
      }
      if (this.viewModel.filters.street) {
        args.street_name = this.viewModel.filters.street;
      }

      if (this.viewModel.filters.door) {
        args.door = this.viewModel.filters.door;
      }
      if (this.viewModel.filters.floor) {
        args.floor = this.viewModel.filters.floor;
      }
      if (this.viewModel.filters.nif) {
        args.nif = this.viewModel.filters.nif;
      }
    } else if (this.viewModel.cpeForm.value.way === 'counter') {
      if (this.viewModel.filters.counter) {
        args.counter = this.viewModel.filters.counter;
      }
      if (this.viewModel.filters.reference) {
        args.reference = this.viewModel.filters.reference;
      }
    } else {
      this.openNotFoundModal();
    }

    if (this.viewModel.filters.acceptTerms) {
      args.acceptTerms = this.viewModel.filters.acceptTerms;
    }

    this.requestCpeService.getCpe(args).subscribe(
      (data) => {
        if (data.length && data.length === 1) {
          this.viewModel.cpeCode = data[0].cpe;

          const modal = this.modalService.custom(this.templateModalCPEfound, this.viewContainerRef);
          modal.onClose().subscribe(() => {
            this.viewModel.hasCopyToClipboard = false;
          });
        } else {
          this.openNotFoundModal();
        }
      },
      (err) => {
        this.openNotFoundModal();
      }
    );
  }

  /**
   * The function is called when the user clicks out of the door input field.
   */
  onFocusOutEvent(): void {
    this.getFloorOption();
  }

  /**
   * It takes a zipcode and a door number, and returns a list of floors.
   */
  getFloorOption(): void {
    if (!this.isLoadingFractions) {
      this.isLoadingFractions = true;
      this.requestCpeService
        .getFractions({
          location: {
            houseNumber: this.viewModel.cpeForm.value.door,
            postalCode: this.viewModel.cpeForm.value.zipcode,
          },
        })
        .pipe(take(1))
        .subscribe((pods) => {
          this.isLoadingFractions = false;
          const floorOptions: DSM3SelectOption[] = [];
          pods.forEach((pod) => {
            if (pod.location.fraction) {
              floorOptions.push({
                value: pod.location.fraction,
                label: pod.location.fraction,
              });
            }
          });
          this.viewModel.floorList$.next(floorOptions);
        });
    }
  }

  /**
   * Funtion to copy to the clipboard the cpe code if the response on getCpeData() has successfully recived
   */
  copyCPEtoClipboard(): void {
    const listener = (e: ClipboardEvent) => {
      const clipboard = e.clipboardData || window['clipboardData'];
      clipboard.setData('text', this.viewModel.cpeCode.toString());
      e.preventDefault();
    };

    document.addEventListener('copy', listener, false);
    document.execCommand('copy');
    document.removeEventListener('copy', listener, false);

    this.viewModel.hasCopyToClipboard = true;
  }

  /*
   * Funciontion to close modal and reset the form
   */
  resetSearch(): void {
    this.viewModel.currentModal.close();
    this.onReset();
  }

  /*
   * Funtion to open the modal CPEnotFound when get cpe code not found or geet some error
   */
  openNotFoundModal(): void {
    this.viewModel.currentModal = this.modalService.custom(this.templateModalCPEnotFound, this.viewContainerRef);
  }
}
